import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { calculateTotalQuantity, calculateTotalAmount } from '@/utils/procurementCalculations';
import { format } from 'date-fns';
import { Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import type { Procurement } from '@/types/database.types';
import ItemQualityReportCard from './ItemQualityReportCard';

interface ProcurementDetailsCardProps {
  procurement: Procurement;
}

const ProcurementDetailsCard: React.FC<ProcurementDetailsCardProps> = ({ procurement }) => {
  const extendedProcurement = procurement as any; // For accessing extended properties
  const quality = extendedProcurement?.quality;
  const itemQualities = extendedProcurement?.item_qualities || [];
  const items = procurement.items || [];
  const isMultiItem = items.length > 1;
  const hasItemQualities = itemQualities.length > 0;
  
  const isPreliminary = quality?.grading_type === 'preliminary';
  const isFinalComplete = quality?.is_final_grading_complete;
  
  // Calculate differences between preliminary and final
  const hasBothGrades = isPreliminary && isFinalComplete;
  const preliminaryCompensation = quality?.preliminary_compensation_kg || 0;
  const finalCompensation = quality?.compensation_kg || 0;
  const compensationDifference = hasBothGrades ? finalCompensation - preliminaryCompensation : 0;

  return (
    <div className="space-y-4">
      {/* Main Details */}
      <Card>
        <CardHeader>
          <CardTitle>Procurement Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Product</Label>
              <p className="text-sm">{procurement.product_name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Status</Label>
              <Badge variant="outline" className="capitalize">
                {procurement.status.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div>
              <Label className="text-sm font-medium">Total Quantity</Label>
              <p className="text-sm font-medium">{calculateTotalQuantity(procurement).toFixed(2)} kg</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Total Amount</Label>
              <p className="text-sm font-medium">Br {calculateTotalAmount(procurement).toFixed(2)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Source Type</Label>
              <p className="text-sm capitalize">{procurement.source_type}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Source Name</Label>
              <p className="text-sm">{procurement.source_name || 'N/A'}</p>
            </div>
          </div>
          
          {/* Requestor Info */}
          <div>
            <Label className="text-sm font-medium">Requested By</Label>
            <p className="text-sm">
              {procurement.requestor ? 
                `${procurement.requestor.first_name} ${procurement.requestor.last_name} (${procurement.requestor.role})` : 
                'Unknown'
              }
            </p>
          </div>

          {/* Expected Delivery */}
          {procurement.expected_delivery_date && (
            <div>
              <Label className="text-sm font-medium">Expected Delivery Date</Label>
              <p className="text-sm">{format(new Date(procurement.expected_delivery_date), 'PPP')}</p>
            </div>
          )}

          {procurement.notes && (
            <div>
              <Label className="text-sm font-medium">Notes</Label>
              <p className="text-sm">{procurement.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-Item Quality Reports (for multi-product with item-level data) */}
      {isMultiItem && hasItemQualities && (
        <ItemQualityReportCard
          items={items}
          itemQualities={itemQualities}
          isPreliminary={isPreliminary}
          isFinalComplete={isFinalComplete}
        />
      )}

      {/* Consolidated Quality Inspection Findings (for single-item or missing item-level data) */}
      {quality && (!isMultiItem || !hasItemQualities) && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge variant="outline" className="bg-blue-100 text-blue-800">Quality Approved</Badge>
              {isPreliminary ? (
                isFinalComplete ? (
                  <Badge className="bg-green-600">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Final Complete
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                    <Clock className="h-3 w-3 mr-1" />
                    Preliminary
                  </Badge>
                )
              ) : (
                <Badge className="bg-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Final
                </Badge>
              )}
              Quality Inspection Findings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Preliminary Assessment */}
            {isPreliminary && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <h4 className="font-medium text-amber-800 mb-2">
                  📋 Preliminary Assessment (Sample-based)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-amber-700">Estimated Damage</Label>
                    <p className="text-lg font-bold text-amber-600">
                      {quality.preliminary_damage_kg || 0} KG
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-amber-700">Estimated Compensation</Label>
                    <p className="text-lg font-bold text-amber-600">
                      {preliminaryCompensation} KG
                    </p>
                  </div>
                </div>
                {!isFinalComplete && quality.final_grading_expected_date && (
                  <p className="text-xs text-amber-600 mt-2">
                    Expected Final Grading: {format(new Date(quality.final_grading_expected_date), 'PPP')}
                  </p>
                )}
              </div>
            )}

            {/* Final Assessment */}
            {(isFinalComplete || !isPreliminary) && (quality.damage_kg > 0 || quality.compensation_kg > 0) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <h4 className="font-medium text-green-800 mb-2">
                  ✅ {isPreliminary ? 'Final Assessment (Complete)' : 'Quality Assessment'}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-green-700">
                      {isPreliminary ? 'Actual Damage' : 'Damage Found'}
                    </Label>
                    <p className="text-lg font-bold text-destructive">
                      {quality.damage_kg || 0} KG
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-green-700">
                      {isPreliminary ? 'Actual Compensation' : 'Agreed Compensation'}
                    </Label>
                    <p className="text-lg font-bold text-orange-600">
                      {finalCompensation} KG
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reconciliation Alert */}
            {hasBothGrades && compensationDifference !== 0 && (
              <div className={`p-3 rounded-lg ${compensationDifference > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                <h4 className={`font-medium mb-2 flex items-center gap-2 ${compensationDifference > 0 ? 'text-red-800' : 'text-green-800'}`}>
                  <AlertTriangle className="h-4 w-4" />
                  Reconciliation Required
                </h4>
                <div className={`text-sm ${compensationDifference > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  <p><span className="font-medium">Difference:</span> {Math.abs(compensationDifference)} kg</p>
                  <p className="mt-1">
                    {compensationDifference > 0 
                      ? `⚠️ Final damage was ${Math.abs(compensationDifference)} kg MORE than estimated.`
                      : `✅ Final damage was ${Math.abs(compensationDifference)} kg LESS than estimated.`
                    }
                  </p>
                </div>
              </div>
            )}
            
            {/* Net Payable Calculation */}
            {(quality.compensation_kg > 0 || preliminaryCompensation > 0) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <Label className="text-sm font-medium text-green-800">
                  {isPreliminary && !isFinalComplete ? 'Suggested Net Payable (Preliminary)' : 'Net Payable'}
                </Label>
                <p className="text-xl font-bold text-green-700">
                  {(calculateTotalQuantity(procurement) - (isPreliminary && !isFinalComplete ? preliminaryCompensation : finalCompensation)).toFixed(2)} KG
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Original: {calculateTotalQuantity(procurement).toFixed(2)} KG - Compensation: {isPreliminary && !isFinalComplete ? preliminaryCompensation : finalCompensation} KG
                </p>
              </div>
            )}

            {quality.damage_notes && (
              <div>
                <Label className="text-sm font-medium">Quality Notes</Label>
                <p className="text-sm text-muted-foreground">{quality.damage_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment Terms & Damage Policies */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Terms & Policies</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Payment Type</Label>
              <Badge variant={procurement.finance?.payment_term_type === 'upfront' ? 'default' : 'secondary'}>
                {procurement.finance?.payment_term_type === 'upfront' ? 'Upfront Payment' : 'Payment Terms'}
              </Badge>
            </div>
            {procurement.finance?.payment_term_days && procurement.finance.payment_term_days > 0 && (
              <div>
                <Label className="text-sm font-medium">Payment Term Days</Label>
                <p className="text-sm">{procurement.finance.payment_term_days} days</p>
              </div>
            )}
            {procurement.finance?.payment_due_date && (
              <div>
                <Label className="text-sm font-medium">Payment Due Date</Label>
                <p className="text-sm">{format(new Date(procurement.finance.payment_due_date), 'PPP')}</p>
              </div>
            )}
          </div>

          {/* Damage Return Policies */}
          <div className="border-t pt-4">
            <Label className="text-sm font-medium mb-2 block">Damage Return Policies</Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Supports Damage Return</Label>
                <p className="text-sm">
                  {extendedProcurement?.source_damage_policies?.supports_damage_return || 
                   procurement.finance?.supports_damage_return ? 
                   'Yes' : 'No'}
                </p>
              </div>
              {(extendedProcurement?.source_damage_policies?.damage_return_percentage || 
                procurement.finance?.damage_return_percentage) && (
                <div>
                  <Label className="text-xs text-muted-foreground">Damage Return %</Label>
                  <p className="text-sm">
                    {extendedProcurement?.source_damage_policies?.damage_return_percentage || 
                     procurement.finance?.damage_return_percentage}%
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Product Items Details */}
      {procurement.items && procurement.items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Product Items ({procurement.items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {procurement.items.map((item: any, index: number) => (
                <div key={item.id || index} className="border rounded-lg p-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <Label className="text-xs text-muted-foreground">Product</Label>
                      <p className="font-medium">{item.product_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Quantity</Label>
                      <p>{item.quantity} kg</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Quality Grade</Label>
                      <p>{item.quality_grade || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Price per Unit</Label>
                      <p>Br {item.price_per_unit}</p>
                    </div>
                    {item.notes && (
                      <div className="col-span-2">
                        <Label className="text-xs text-muted-foreground">Notes</Label>
                        <p className="text-xs">{item.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProcurementDetailsCard;
